import { AxiosError } from "axios";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { CitiesSelect } from "./CitiesSelect";
import { KeywordsSelect } from "./KeywordsSelect";
import DataSvc from "../../../../services/dataSvc";
import { buildCsvFilename } from "../../../../utils/csvUtils";
import { TextFileDownload } from "../../../Common/TextFileDownload";

import "./styles.scss";

interface ICheckOwnerAreaProps extends RouteComponentProps<any> {
  onLoading(loadingText: string | void): void;
  onLoaded(): void;
}
const CheckOwnerArea: React.FunctionComponent<ICheckOwnerAreaProps> = ({
  onLoading,
  onLoaded,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`internaltoolsPage.checkOwnerArea.${key}`);
  const [prefecture, setPrefecture] = useState<string>("");
  const [cities, setCities] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<{
    common: string[];
    custom: string[];
  }>({ common: [], custom: [] });
  const [errorMessage, setErrorMessage] = useState<string>("");

  const downloadCsv = async (): Promise<{
    filename: string;
    content: string;
  }> => {
    if (!prefecture) {
      setErrorMessage(t("set_prefecture"));
      throw new Error(t("set_prefecture"));
    }
    if (cities.length === 0) {
      setErrorMessage(t("set_municipalities"));
      throw new Error(t("set_municipalities"));
    }
    if (keywords.common.length === 0 && keywords.custom.length === 0) {
      setErrorMessage(t("set_keywords"));
      throw new Error(t("set_keywords"));
    }

    onLoading();

    try {
      const data = await DataSvc.checkOwnerArea({
        prefecture,
        cities,
        keywords: [...keywords.common, ...keywords.custom],
      });

      const { csvString } = data;

      if (!csvString) {
        throw new Error("不明なエラー発生");
      }

      setErrorMessage("");

      const filename = buildCsvFilename(
        {
          facilityName: `${prefecture}_${cities.join("-")}`,
          contentDescription: "check_owner_area",
        },
        _t
      );

      return {
        filename,
        content: csvString,
      };
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        setErrorMessage(t("quota_exceeded"));
      } else {
        setErrorMessage(
          _t("internaltoolsPage.public.error", { errorMessage: error })
        );
      }
      throw new Error("不明なエラー発生");
    } finally {
      onLoaded();
    }
  };

  return (
    <React.Fragment>
      <h1 className="main-title">{t("main_title")}</h1>
      <p className="internal-note">{t("internal_note")}</p>
      <section className="card check-owner-area-input">
        <p>{t("note")}</p>
        <h3>{t("section_1_title")}</h3>
        <input
          placeholder={t("section_1_prefecture")}
          onChange={(e) => setPrefecture(e.target.value)}
        />
        <br />
        <CitiesSelect selectedCities={cities} setSelectedCities={setCities} />
        <br />
        <h3>{t("section_2_title")}</h3>
        <KeywordsSelect keywords={keywords} setKeywords={setKeywords} />
        <TextFileDownload
          queryKey={["checkOwnerArea"]}
          queryFunction={downloadCsv}
        />
        {errorMessage && <h3 id="error">{errorMessage}</h3>}
      </section>
    </React.Fragment>
  );
};

export default withRouter(CheckOwnerArea);
